import { safeStorage, createToggleRow } from '@learnforge/demo-kit';
import type { CustomExperimentSpec, RenderHandle } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import { createActionBar, type ActionBarButton } from '../action-bar';
import { createTree, type TreeNode, type TreeHandle } from '../render/tree';
import { createSvgEl } from '@learnforge/demo-render';
import { convergentsOf } from './number-line-surgeon';
import { POWER_PRESETS } from '../data/power-presets';
import type { ArithContext, ArithKey } from '../types';

// Integer exponents use exact base**exp (incl. negatives); fractional exponents
// fall back to exp(exponent * ln base).
export function treeLeafCount(base: number, exponent: number): number {
  if (Number.isInteger(exponent)) return base ** exponent;
  return Math.exp(exponent * Math.log(base));
}

// A COMPLETE base-ary tree, capped by DEPTH so its breadth (base^depth) stays
// within maxRenderedLeaves — instead of skipping nodes mid-tree. When the true
// power is deeper than the cap, the bottom nodes become "summary" nodes whose
// value is how many leaves each stands for (value > 1), so nothing is dropped
// silently. Each node's value is base**(remaining depth); the root shows the
// true total (which also covers negative/fractional exponents, e.g. 10^-3).
export function buildTree(
  base: number,
  exponent: number,
  maxRenderedLeaves = 64,
): TreeNode<number> {
  const fullDepth = Math.max(0, Math.floor(exponent));
  // If the whole tree fits, draw every node to full depth. Otherwise prune each
  // node to first + "…" + last (effective branching 2) and cap depth so the
  // spine fits — this still shows EVERY level (e.g. 10^6: 1000000 → 100000 … →
  // … → 1), without building a million nodes.
  const fitsWhole = base ** fullDepth <= maxRenderedLeaves;
  const renderDepth = fitsWhole
    ? fullDepth
    : Math.min(fullDepth, Math.max(1, Math.floor(Math.log(maxRenderedLeaves) / Math.log(2))));
  const prune = !fitsWhole && base > 2;
  function build(layer: number): TreeNode<number> {
    const value = base ** (fullDepth - layer);
    if (layer >= renderDepth) return { data: value, children: [] };
    const children: TreeNode<number>[] = prune
      ? [build(layer + 1), { data: 0, children: [], ellipsis: true }, build(layer + 1)]
      : Array.from({ length: base }, () => build(layer + 1));
    return { data: value, children };
  }
  return { ...build(0), data: treeLeafCount(base, exponent) };
}

export interface Scientific {
  mantissa: number;
  exponent: number;
}

export function scientificNotation(value: number): Scientific {
  if (value === 0) return { mantissa: 0, exponent: 0 };
  const exponent = Math.floor(Math.log10(Math.abs(value)));
  const mantissa = value / 10 ** exponent;
  return { mantissa, exponent };
}

// base^(irrational exp) approximated through the last continued-fraction convergent p/q of exp.
export function irrationalExponentApprox(base: number, exp: number, depth: number): number {
  const convs = convergentsOf(exp, depth);
  const last = convs[convs.length - 1];
  if (last === undefined) return base ** exp;
  return base ** (last.num / last.den);
}

interface State {
  base: number;
  exponent: number;
  advanced: boolean;
  selectedLevel: number | null;
}

const ADVANCED_KEY = 'arithmetic.power-tower.advanced';

function buildSpec(): CustomExperimentSpec<State, ArithKey, ArithContext> {
  return {
    labelKey: 'experiment.power-tower.label' as ArithKey,
    demoId: 'arithmetic',
    advancedKey: 'power-tower',

    initialState(ctx): State {
      return {
        base: 2,
        exponent: 4,
        advanced: ctx.advanced,
        selectedLevel: null,
      };
    },

    createRender(ctx, host, controls): RenderHandle<State> {
      // ── Stage SVG ──
      const svg = createSvgEl('svg', { viewBox: '0 0 800 470', class: 'arith-stage' });
      host.appendChild(svg);
      const treeGroup = createSvgEl('g', { transform: 'translate(0, 10)' });
      svg.appendChild(treeGroup);
      const readout = createSvgEl('text', {
        class: 'arith-readout',
        x: 400,
        y: 396,
        'text-anchor': 'middle',
      });
      svg.appendChild(readout);
      // Two stacked relation lines, populated when a node is clicked.
      const levelLine1 = createSvgEl('text', {
        class: 'arith-ratio-eq',
        x: 400,
        y: 424,
        'text-anchor': 'middle',
      });
      const levelLine2 = createSvgEl('text', {
        class: 'arith-ratio-eq',
        x: 400,
        y: 448,
        'text-anchor': 'middle',
      });
      svg.appendChild(levelLine1);
      svg.appendChild(levelLine2);

      // ── Tree handle (closure var; destroyed + recreated on each update) ──
      let treeHandle: TreeHandle<number> | null = null;

      // ── Advanced panel inputs (null when panel is in foundation mode) ──
      let baseInput: HTMLInputElement | null = null;
      let expInput: HTMLInputElement | null = null;

      // ── Action bar — built ONCE ──
      const actionBar = createActionBar(
        ctx.actionBar,
        POWER_PRESETS.map((p) => ({
          id: p.id,
          labelKey: `${p.base}^${p.exponent}`,
          active: p.base === 2 && p.exponent === 4,
          onClick() {
            controls.setState((s) => ({
              ...s,
              base: p.base,
              exponent: p.exponent,
              selectedLevel: null,
            }));
          },
        })),
      );

      // ── Panel helpers ──
      function makeNumberInput(
        which: 'base' | 'exponent',
        label: string,
        min: number,
        max: number,
      ): HTMLElement {
        const wrap = document.createElement('div');
        wrap.className = 'arith-frac-input';
        const lbl = document.createElement('label');
        lbl.textContent = label;
        const input = document.createElement('input');
        input.type = 'number';
        input.min = String(min);
        input.max = String(max);
        input.step = '1';
        input.addEventListener('change', () => {
          const v = Math.floor(Number(input.value));
          if (Number.isFinite(v) && v >= min && v <= max) {
            controls.setState((s) => ({ ...s, [which]: v, selectedLevel: null }));
          }
        });
        if (which === 'base') baseInput = input;
        else expInput = input;
        wrap.appendChild(lbl);
        wrap.appendChild(input);
        return wrap;
      }

      const advToggle = createToggleRow({
        i18n: ctx.i18n,
        labelKey: 'btn.advanced' as ArithKey,
        checked: ctx.advanced,
        onChange(next) {
          safeStorage.set(ADVANCED_KEY, next ? 'true' : 'false');
          controls.setState((s) => ({ ...s, advanced: next }));
        },
      });

      // Build panel once per advanced-mode change (mirroring old renderPanel logic).
      function buildPanel(advanced: boolean): void {
        baseInput = null;
        expInput = null;
        ctx.panel.replaceChildren();
        if (advanced) {
          ctx.panel.appendChild(makeNumberInput('base', 'base', 2, 12));
          ctx.panel.appendChild(makeNumberInput('exponent', 'exp', 0, 16));
        }
        ctx.panel.appendChild(advToggle.element);
      }

      // Build panel immediately with the initial advanced value from ctx.
      buildPanel(ctx.advanced);

      // ── RenderHandle ──
      let lastAdvanced: boolean = ctx.advanced;

      const update = (state: State): void => {
        // Rebuild panel only when advanced flag changes.
        if (state.advanced !== lastAdvanced) {
          lastAdvanced = state.advanced;
          buildPanel(state.advanced);
        }

        // Destroy previous tree, recreate with current highlight.
        treeHandle?.destroy();
        treeHandle = createTree<number>(treeGroup, {
          root: buildTree(state.base, state.exponent),
          renderNode: (node) => ({
            label: String(node.data),
            className: node.children.length === 0 && node.data > 1 ? 'summary' : '',
          }),
          width: 800,
          height: 360,
          highlightLevel: state.selectedLevel,
          onNodeClick(node, depth) {
            const v = node.data;
            const root = treeLeafCount(state.base, state.exponent);
            const d = state.base > 1 && v > 0 ? Math.round(Math.log(v) / Math.log(state.base)) : 0;
            // Update level lines immediately (imperative; no state round-trip needed).
            levelLine1.textContent = `${v} = ${state.base}^${d}`;
            if (depth === 0) {
              levelLine2.textContent = '';
            } else if (d > 0 && state.exponent % d === 0) {
              levelLine2.textContent = `${v}^${state.exponent / d} = ${root}`;
            } else {
              levelLine2.textContent = `${Math.round(root / v)} × ${v} = ${root}`;
            }
            controls.setState((s) => ({ ...s, selectedLevel: depth }));
          },
        });

        // Readout text.
        const v = treeLeafCount(state.base, state.exponent);
        const large =
          !Number.isSafeInteger(v) || Math.abs(v) >= 1e6 || (v !== 0 && Math.abs(v) < 1e-3);
        if (large) {
          const s = scientificNotation(v);
          readout.textContent = `${state.base}^${state.exponent} ≈ ${s.mantissa.toFixed(3)} × 10^${s.exponent}`;
        } else {
          readout.textContent = `${state.base}^${state.exponent} = ${v}`;
        }

        // Level-line text — clear when no level is selected; leave alone when set
        // (the onNodeClick handler wrote them; a re-render from selectedLevel change
        // must NOT wipe what was just written).
        if (state.selectedLevel === null) {
          levelLine1.textContent = '';
          levelLine2.textContent = '';
        }

        // Update action bar active state.
        actionBar.setButtons(
          POWER_PRESETS.map(
            (p): ActionBarButton => ({
              id: p.id,
              labelKey: `${p.base}^${p.exponent}`,
              active: p.base === state.base && p.exponent === state.exponent,
              onClick() {
                controls.setState((s) => ({
                  ...s,
                  base: p.base,
                  exponent: p.exponent,
                  selectedLevel: null,
                }));
              },
            }),
          ),
        );

        // Guard number-input value sync.
        if (baseInput !== null && baseInput.value !== String(state.base)) {
          baseInput.value = String(state.base);
        }
        if (expInput !== null && expInput.value !== String(state.exponent)) {
          expInput.value = String(state.exponent);
        }
      };

      return {
        update,
        dispose() {
          treeHandle?.destroy();
          actionBar.destroy();
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());
export const mountPowerTower = experiment.mount.bind(experiment);
